import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, MapPin, Clock, Trophy, Users, Flag, ArrowRight, Heart, Award } from "lucide-react";
import api from "@/lib/api";
import { PageShell } from "@/components/PageShell";

const SILHOUETTE = "https://images.unsplash.com/photo-1636315662384-8406112deaf1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const PORTRAIT = "https://static.prod-images.emergentagent.com/jobs/5306c248-bca6-4547-8c45-6083734dd662/images/f49df9ddd69e6fe27e7503ccb40e6f37ccf5b9fb6ec8076f1488689719f962d8.png";

const fade = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] } }) };

export default function Home() {
  const [event, setEvent] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [donations, setDonations] = useState({ total: 0 });

  useEffect(() => {
    api.get("/event").then((r) => setEvent(r.data));
    api.get("/announcements").then((r) => setAnnouncements(r.data.slice(0, 2)));
    api.get("/donations/total").then((r) => setDonations(r.data));
  }, []);

  return (
    <PageShell hero>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <div className="absolute inset-0 sky-gradient" />
        <img src={SILHOUETTE} alt="Golfer at sunset" className="absolute inset-0 h-full w-full object-cover mix-blend-overlay opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-sky-900/30" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pb-20 pt-32 w-full">
          <motion.p variants={fade} initial="hidden" animate="show" custom={0} className="inline-flex items-center gap-2 text-white/90 text-sm font-medium bg-white/10 backdrop-blur px-4 py-1.5 rounded-full border border-white/20">
            <Heart size={14} className="text-green-300" /> In loving memory of Pow
          </motion.p>
          <div className="flex items-center gap-8">
            <motion.h1 variants={fade} initial="hidden" animate="show" custom={1} className="mt-6 font-display text-4xl sm:text-5xl lg:text-7xl text-white leading-[1.05] max-w-4xl drop-shadow-lg">
              The Darren Power Memorial Golf Classic <span className="text-sky-200">2026</span>
            </motion.h1>
            <motion.img
              variants={fade} initial="hidden" animate="show" custom={1}
              src={PORTRAIT}
              alt="Darren 'Pow' Power"
              data-testid="hero-portrait"
              className="hidden md:block h-44 w-44 lg:h-56 lg:w-56 shrink-0 rounded-full object-cover ring-4 ring-white/60 shadow-2xl"
            />
          </div>
          <motion.p variants={fade} initial="hidden" animate="show" custom={2} className="mt-6 text-base sm:text-lg text-white/90 leading-relaxed max-w-2xl">
            {event?.subtitle || "Join us for a golf day outing in memory of our great friend."}
          </motion.p>

          <motion.div variants={fade} initial="hidden" animate="show" custom={3} className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" data-testid="hero-register-btn" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-green-600 text-white font-semibold shadow-xl hover:-translate-y-0.5 hover:shadow-2xl transition-all">
              Enter the Classic <ArrowRight size={18} />
            </Link>
            <Link to="/leaderboard" data-testid="hero-leaderboard-btn" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full glass text-slate-900 font-semibold hover:-translate-y-0.5 transition-all">
              <Trophy size={18} /> Live Leaderboard
            </Link>
            <Link to="/donate" data-testid="hero-donate-btn" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/10 border border-white/30 text-white font-semibold hover:bg-white/20 transition-all">
              <Heart size={18} /> Donate
            </Link>
          </motion.div>

          <motion.div variants={fade} initial="hidden" animate="show" custom={4} className="mt-12 grid sm:grid-cols-3 gap-3 max-w-3xl">
            {[
              { icon: Calendar, label: event?.date || "Sat 29th Aug 2026" },
              { icon: MapPin, label: event?.location || "Elmgreen GC, Dublin" },
              { icon: Clock, label: `Tee-times ${event?.tee_times || "10:10am–3pm"}` },
            ].map((it, i) => (
              <div key={i} className="glass rounded-2xl px-5 py-4 flex items-center gap-3">
                <it.icon size={20} className="text-sky-600 shrink-0" />
                <span className="text-sm font-semibold text-slate-800">{it.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FORMAT / COST BENTO */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-3xl bg-slate-900 text-white p-10 relative overflow-hidden">
            <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-sky-500/20 blur-2xl" />
            <Flag className="text-green-400 mb-5" size={28} />
            <h2 className="font-display text-3xl lg:text-4xl">The Format</h2>
            <p className="mt-4 text-slate-300 text-lg max-w-xl leading-relaxed">
              {event?.format || "Four person team event. Enter as a team, partial team or an individual."}
            </p>
            <div className="mt-8 flex flex-wrap gap-6">
              <div><p className="text-4xl font-display text-sky-300">€55</p><p className="text-sm text-slate-400 mt-1">per player</p></div>
              <div className="w-px bg-white/15" />
              <div><p className="text-4xl font-display text-green-300">€220</p><p className="text-sm text-slate-400 mt-1">per team</p></div>
              <div className="w-px bg-white/15" />
              <div><p className="text-4xl font-display text-white">€{Math.round(donations.total)}</p><p className="text-sm text-slate-400 mt-1">raised so far</p></div>
            </div>
          </div>

          <div className="rounded-3xl bg-secondary p-10 flex flex-col justify-between">
            <div>
              <Award className="text-sky-600 mb-5" size={28} />
              <h3 className="font-display text-2xl text-slate-900">Prizes</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">Prizes awarded to the winning team and runners-up, plus individual prizes for longest drive and nearest-the-pin.</p>
            </div>
            <Link to="/register" className="mt-6 inline-flex items-center gap-2 text-sky-700 font-semibold hover:gap-3 transition-all">
              Secure your place <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        {/* Schedule */}
        <div className="mt-6 grid md:grid-cols-4 gap-4">
          {(event?.schedule || []).map((s, i) => (
            <div key={i} className="rounded-2xl bg-white border border-slate-100 p-6 hover:-translate-y-1 hover:shadow-lg transition-all">
              <p className="font-display text-2xl text-sky-700">{s.time}</p>
              <p className="mt-1.5 text-sm text-slate-600">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MEMORIAL STRIP */}
      <section className="relative bg-slate-950 noise text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <img src={event?.portrait} alt="Darren Power" className="rounded-3xl w-full max-w-sm object-cover shadow-2xl ring-1 ring-white/10" />
          </div>
          <div>
            <p className="text-sky-400 text-sm uppercase tracking-[0.3em] font-semibold">Remembering</p>
            <h2 className="mt-4 font-display text-4xl lg:text-5xl leading-tight">A day for Pow</h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed font-display italic">
              "{event?.tribute || "A true friend is never truly gone. His spirit lives on in the memories of those who love him."}"
            </p>
            <Link to="/memorial" data-testid="home-memorial-link" className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-slate-900 font-semibold hover:-translate-y-0.5 transition-all">
              Visit the memorial <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ANNOUNCEMENTS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
        <div className="flex items-center gap-3 mb-8">
          <Users className="text-green-600" />
          <h2 className="font-display text-3xl text-slate-900">Latest Updates</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {announcements.map((a) => (
            <div key={a.id} data-testid="home-announcement" className="rounded-2xl border border-slate-100 bg-white p-7 hover:shadow-lg transition-shadow">
              <h3 className="font-semibold text-lg text-slate-900">{a.title}</h3>
              <p className="mt-2 text-slate-600 leading-relaxed">{a.body}</p>
            </div>
          ))}
          {announcements.length === 0 && <p className="text-slate-500">No updates yet.</p>}
        </div>
      </section>
    </PageShell>
  );
}
